// Site-wide editable page content.
// Every page defines its editable fields plus the default copy shown until an admin
// overrides it from the Admin Portal → "Page Content" tab.
// Field types: text | textarea | paragraphs (blank line = new paragraph -> string[])
//              list (one per line -> string[]) | pairs (Title | Note per line -> {title,note}[])
//              groups (structured: {title, items[]}[])

export const PAGES = {
  home: {
    label: "Home",
    fields: [
      { key: "heroLine1", label: "Hero — Line 1", type: "text" },
      { key: "heroLine2", label: "Hero — Line 2", type: "text" },
      { key: "heroWelcome", label: "Hero — Welcome Paragraph", type: "textarea", rows: 4 },
      { key: "presidentOpening", label: "President's Note — Opening (always visible)", type: "paragraphs" },
      { key: "presidentRest", label: "President's Note — Read More Section", type: "paragraphs", rows: 8 },
      { key: "presidentName", label: "President — Signature Name", type: "text" },
      { key: "presidentRoleLine", label: "President — Role Line", type: "text" },
      { key: "whoWeAre", label: "Who We Are — Paragraphs", type: "paragraphs", rows: 10 },
    ],
    defaults: {
      heroLine1: "Rotaract Club of",
      heroLine2: "Sophia College for Women",
      heroWelcome:
        "Welcome to the Rotaract Club of Sophia College for Women — a space where young minds come together to learn, lead, serve and create meaningful change. This year marks a journey of growth for us, with our community crossing 150+ members and continuing to grow through new ideas, initiatives and opportunities.",
      presidentOpening: [
        "It is an honour to serve as the 11th President of the Rotaract Club of Sophia College for Women for the Rotary Year 2026–27.",
        "This year, under the theme “Forged: Reclaiming the Fire,” the aim is to create a year that gives every member the opportunity to participate, contribute, learn and grow. Being forged is about becoming stronger through every experience, and this year is about making those experiences meaningful.",
      ],
      presidentRest: [
        "With our vision to “do less, but do better,” the focus will be on creating projects and initiatives with purpose, encouraging genuine member involvement and ensuring that every opportunity adds something valuable to the Rotaract experience. Whether through service, professional development, creativity, entrepreneurship, fellowship or simply being part of the community, there should be a place for everyone.",
        "A perfect year cannot be promised. There will be mistakes, setbacks and days when we may question why we signed up for this. But there will also be moments worth remembering, people worth knowing and experiences that shape us in ways we may not expect. We will learn together, grow together, celebrate together, and no one will have to go through this journey alone.",
        "To every General Body Member and the Board of RCSCW, this year belongs to all of us. Here's to showing up, taking chances, making memories and reclaiming the fire together.",
      ],
      presidentName: "Rtr. Shahana Paloji",
      presidentRoleLine: "President, 2026–27 · Rotaract Club of Sophia College for Women",
      whoWeAre: [
        "The Rotaract Club of Sophia College for Women, chartered in 2017, proudly functions under Rotaract District 3141 as the youth wing of the Rotary Club of Bombay Central. Entering its eleventh year of service, the club continues to grow as a dynamic, women-led organisation rooted in the values of service, leadership, fellowship and community engagement.",
        "Over the years, RCSCW has created a strong identity through its dedicated members and diverse initiatives, providing young women with meaningful opportunities to lead, serve and grow. Through community service, professional development, entrepreneurship, fellowship and various creative and social initiatives, the club strives to turn ideas into action and experiences into lasting learning.",
        "For Rotary Year 2026–27, under the theme “Forged: Reclaiming the Fire,” the club enters a new chapter focused on strengthening what has been built while creating an even greater impact. With a growing community of 150+ members, this year is centred on meaningful involvement, purposeful initiatives and creating opportunities for every member to find their place and contribute.",
        "With a vision to do less, but do better, RCSCW aims to create initiatives that go beyond numbers and leave a lasting impact. This year, we continue to forge stronger connections, reclaim our collective fire and empower young women to lead with purpose, serve with consistency and create change that extends far beyond the club.",
      ],
    },
  },

  about: {
    label: "About Us",
    fields: [
      { key: "heroText", label: "Hero Intro", type: "textarea" },
      { key: "whatIsRotaract", label: "What is Rotaract — Description", type: "textarea", rows: 4 },
      { key: "rcscwLead", label: "Paragraph 1 — Lead", type: "text" },
      { key: "rcscwBody", label: "Paragraph 1 — Body", type: "textarea" },
      { key: "connectionLead", label: "Paragraph 2 — Lead", type: "text" },
      { key: "connectionBody", label: "Paragraph 2 — Body", type: "textarea" },
      { key: "areasLead", label: "Paragraph 3 — Lead", type: "text" },
      { key: "areasBody", label: "Paragraph 3 — Body", type: "textarea" },
      { key: "focusAreas", label: "Ten Avenues (one per line: Title | Description)", type: "pairs", rows: 11 },
    ],
    defaults: {
      heroText:
        "Rotaract (Rotary in Action) is a global movement of young leaders. At Sophia College for Women, that movement has found a home with a fire of its own.",
      whatIsRotaract:
        "Rotaract is a Rotary International service club for young adults aged 18 and above. Rotaractors across the world work through their local clubs to serve their communities, develop professionally, build international connections and grow as leaders, all while having a lot of fun along the way.",
      rcscwLead: "What is RCSCW?",
      rcscwBody:
        "The Rotaract Club of Sophia College for Women was chartered on 1st February 2017 and has grown into one of the most active clubs on campus, a community that has now crossed 150+ members.",
      connectionLead: "Our Rotary connection.",
      connectionBody:
        "We are proudly sponsored by the Rotary Club of Bombay Central, working under the banner of Rotary International District 3141. Our Rotarians guide, mentor and celebrate everything we do.",
      areasLead: "Our areas of work.",
      areasBody:
        "Ten avenues shape our year: Community Service, Club Service, International Service, Partners-in-Service, Entrepreneurship & Professional Development, Sports, Public Relations & Marketing, Publications, Digital Media and Zonal Coordinator. Every member finds a place to belong, and a place to lead.",
      focusAreas: [
        { title: "Community Service", note: "Driving change on campus and beyond through hands-on service." },
        { title: "Club Service", note: "Strengthening the club from within through its members and operations." },
        { title: "International Service", note: "Building bridges and impact that reach beyond borders." },
        { title: "Partners-in-Service", note: "Collaborating with Rotary and partner clubs on shared initiatives." },
        { title: "Entrepreneurship & Professional Development", note: "Ideas, startups, workshops and skills that prepare members for careers." },
        { title: "Sports", note: "Team spirit, fitness and friendly competition across the year." },
        { title: "Public Relations & Marketing", note: "Telling the club's story with design, content and outreach." },
        { title: "Publications", note: "Elysian, newsletters and everything the club puts in print." },
        { title: "Digital Media", note: "Photography, reels and the club's digital presence." },
        { title: "Zonal Coordinator", note: "Connecting RCSCW with the zone and district Rotaract movement." },
      ],
    },
  },

  legacy: {
    label: "Our Legacy",
    fields: [
      { key: "heroText", label: "Hero Intro", type: "textarea", rows: 5 },
      { key: "closingYear", label: "Closing — Year Line", type: "text" },
      { key: "closingTheme", label: "Closing — Theme", type: "text" },
      { key: "closingNote", label: "Closing — Note", type: "textarea" },
    ],
    defaults: {
      heroText:
        "Every chapter of RCSCW has been shaped by a leader who stepped forward, took responsibility, and left their mark on the club. Our Past Presidents are an integral part of this journey, each bringing their own vision, leadership, and contribution to help build the club we know today. As we move forward, we carry with us the lessons, traditions, and foundation laid by those who led before us.",
      closingYear: "2026–27 — ",
      closingTheme: "Forged: Reclaiming the Fire",
      closingNote: "The fire continues. The next chapter of this legacy is being written by 150+ members — right now.",
    },
  },

  "north-star": {
    label: "Our North Star",
    fields: [
      { key: "heroText", label: "Hero Intro", type: "textarea" },
      { key: "themeTitle", label: "Theme — Title", type: "text" },
      { key: "themeSubtitle", label: "Theme — Subtitle", type: "text" },
      { key: "yearBanner", label: "Year Banner — Image", type: "image" },
      { key: "manifesto", label: "Theme Manifesto (blank line = new paragraph)", type: "paragraphs", rows: 12 },
      { key: "manifestoClosing", label: "Manifesto — Closing Line", type: "textarea" },
      { key: "visionQuoteA", label: "Vision Quote — Part 1", type: "text" },
      { key: "visionQuoteB", label: "Vision Quote — Part 2", type: "text" },
      { key: "visionText", label: "Vision — Description", type: "textarea" },
      { key: "goals", label: "Goals (one per line)", type: "list", rows: 12 },
    ],
    defaults: {
      heroText:
        "Every year needs a direction. Ours is a single point of light we return to in every decision, every project and every late-night planning call.",
      themeTitle: "Forged:",
      themeSubtitle: "Reclaiming the Fire",
      yearBanner: "",
      manifesto: [
        "Every legacy is shaped by the people who carry it forward. By the experiences that define it, the challenges that test it, and the people who continue to believe in what it can become. Today, we step into that legacy with a vision of our own.",
        "Forged: Reclaiming the Fire is not just a theme; it is a declaration. It is the story of ordinary students coming together to reignite a forgotten flame. We are not here to preserve what once was; we are here to rebuild, reshape, and redefine.",
        "With passion as our fire and determination as our steel, we aim to forge the Rotaract Club of Sophia College for Women into something extraordinary. Not a relic of the past, but a force of the present and future.",
        "To be forged is to be tested, shaped, and strengthened. And as we step into this year, we carry with us everything that has brought this club here, while having the courage to take it somewhere new.",
        "We are the spark. We are the fire. And we are just getting started.",
      ],
      manifestoClosing: "Because, as always.. you can be the king, but we are the ACE. We are RC SOPHIAS, We set the PACE",
      visionQuoteA: "“Do less,",
      visionQuoteB: "but do better.”",
      visionText:
        "The vision for the club is very simple, to do less, but do better. Focus more on meaningful impact, stronger connections & a culture where every member can learn, lead, create, contribute and truly enjoy their Rotaract journey.",
      goals: [
        "To nurture confident and capable young leaders through meaningful opportunities for growth.",
        "To foster a strong sense of friendship and belonging within the Club.",
        "To undertake meaningful service that responds to genuine community needs.",
        "To provide members with opportunities for professional learning and exposure.",
        "To provide a platform for members to explore their creativity and turn ideas into action.",
        "To ensure every member has the opportunity to participate and take ownership.",
        "To strengthen relationships within the Rotary family through shared experiences and collaboration.",
        "To connect members with the wider Rotaract community at the District and international levels.",
        "To uphold a culture of integrity, respect and inclusivity within the Club.",
        "To promote international understanding through fellowship and cultural exchange.",
        "To build a strong foundation of continuity so that each team strengthens the Club for the next.",
      ],
    },
  },

  "rotary-club": {
    label: "Rotary Club of Bombay Central",
    fields: [
      { key: "heroText", label: "Hero Intro", type: "textarea" },
      { key: "clubPhoto", label: "Club Photograph", type: "image" },
      { key: "rotaractLead", label: "Paragraph 1 — Lead", type: "text" },
      { key: "rotaractBody", label: "Paragraph 1 — Body", type: "textarea" },
      { key: "charterLead", label: "Paragraph 2 — Lead", type: "text" },
      { key: "charterBody", label: "Paragraph 2 — Body", type: "textarea" },
      { key: "history1", label: "History — Paragraph 1", type: "textarea", rows: 5 },
      { key: "history2", label: "History — Paragraph 2", type: "textarea", rows: 5 },
      { key: "history3", label: "History — Paragraph 3", type: "textarea", rows: 5 },
      { key: "card1Title", label: "Card 1 — Title", type: "text" },
      { key: "card1Note", label: "Card 1 — Text", type: "textarea" },
      { key: "card2Title", label: "Card 2 — Title", type: "text" },
      { key: "card2Note", label: "Card 2 — Text", type: "textarea" },
      { key: "card3Title", label: "Card 3 — Title", type: "text" },
      { key: "card3Note", label: "Card 3 — Text", type: "textarea" },
    ],
    defaults: {
      heroText:
        "Every Rotaract club stands on the shoulders of a Rotary club. Ours is the Rotary Club of Bombay Central — our sponsors, mentors and biggest cheerleaders.",
      clubPhoto: "",
      rotaractLead: "Rotary International",
      rotaractBody:
        "is a global network of 1.4 million members who take action to create lasting change in their communities and in themselves. Rotaract is Rotary's movement for young leaders aged 18 and above.",
      charterLead: "The Rotary Club of Bombay Central",
      charterBody:
        "helped charter the Rotaract Club of Sophia College for Women on 1st February 2017 and has stood behind the club ever since.",
      history1:
        "The Rotary Club of Bombay Central was officially chartered on 28 May 1974 as part of Rotary International, a global community organisation dedicated to service and humanitarian development. Since its establishment, the club has worked towards addressing the needs of the local community while contributing to the growth of the Rotary movement in Mumbai. The club has also played a role in Rotary's regional expansion, notably sponsoring the formation of the Rotary Club of Bombay Hanging Garden, which was chartered on 16 May 1986.",
      history2:
        "Over the decades, the Rotary Club of Bombay Central has evolved into an active community-service organisation, undertaking initiatives across education, healthcare, nutrition, infrastructure, vocational development, and community welfare. Its education-focused initiatives support students from underserved communities through assistance for higher education, educational supplies, backpacks, and grants, including collaborations with organisations such as the Sulochana Foundation.",
      history3:
        "The club has also undertaken projects focused on community infrastructure and healthcare, including support for classroom construction and initiatives addressing child nutrition and wellbeing in collaboration with non-profit organisations such as the Samaritan Help Mission. In addition, the club regularly conducts blood donation camps, vocational training initiatives, and distribution drives, extending support to marginalised communities and contributing to broader social development.",
      card1Title: "Our Sponsors",
      card1Note: "The Rotary Club of Bombay Central sponsors RCSCW, guiding our projects, governance and growth.",
      card2Title: "Our Mentors",
      card2Note: "Rotarians who advise the board, attend our events and open doors for our members.",
      card3Title: "District 3141",
      card3Note: "We function under Rotaract District 3141 alongside Rotaract clubs across the region.",
    },
  },

  collaborators: {
    label: "Collaborators & Sponsors",
    fields: [
      { key: "heroText", label: "Hero Intro", type: "textarea" },
      { key: "groups", label: "Groups (e.g. Sponsors, Collaborators)", type: "groups" },
      { key: "emptyTitle", label: "Closing Note — Title", type: "text" },
      { key: "emptyNote", label: "Closing Note — Text", type: "textarea" },
    ],
    defaults: {
      heroText:
        "Nothing we build happens alone. These are the clubs, organisations and partners who have stood with RCSCW through the years.",
      groups: [
        { title: "Sponsors", items: ["[Sponsor Name]", "[Sponsor Name]"] },
        { title: "Collaborators", items: ["[Collaborator Name]", "[Collaborator Name]"] },
      ],
      emptyTitle: "This wall grows as we do",
      emptyNote: "Partner logos and names will be added here as collaborations are confirmed through the year.",
    },
  },

  contact: {
    label: "Contact & Socials",
    fields: [
      { key: "heroText", label: "Hero Intro", type: "textarea" },
      { key: "instagram", label: "Instagram — Handle or Link (e.g. @rcscw or full URL)", type: "text" },
      { key: "linkedin", label: "LinkedIn — Page or Profile URL", type: "text" },
      { key: "email", label: "Email — Address (e.g. official.rcscw@gmail.com)", type: "text" },
      { key: "college", label: "College Website — Note or URL", type: "text" },
      { key: "rotary", label: "Rotary Connection — Note", type: "text" },
      { key: "addressTitle", label: "Address Card — Title", type: "text" },
      { key: "addressLine", label: "Address Card — Line", type: "textarea" },
    ],
    defaults: {
      heroText: "Have a collaboration in mind? Want Rotaract in your life? Reach out — we reply fast and we reply warmly.",
      instagram: "[@rcscw handle]",
      linkedin: "[LinkedIn page]",
      email: "[email address]",
      college: "Sophia College for Women, Mumbai",
      rotary: "Rotary Club of Bombay Central · District 3141",
      addressTitle: "Rotaract Club of Sophia College for Women",
      addressLine:
        "Sophia College for Women, Mumbai · Sponsored by the Rotary Club of Bombay Central · Rotary International District 3141",
    },
  },
};

// ---- text <-> value conversion helpers (shared by admin editor and pages) ----

export function fieldToText(type, value) {
  if (type === "paragraphs") return (value || []).join("\n\n");
  if (type === "list") return (value || []).join("\n");
  if (type === "pairs") return (value || []).map((p) => `${p.title} | ${p.note}`).join("\n");
  return value ?? "";
}

export function textToField(type, text) {
  if (type === "paragraphs") return text.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  if (type === "list") return text.split("\n").map((s) => s.trim()).filter(Boolean);
  if (type === "pairs")
    return text
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((line) => {
        const idx = line.indexOf("|");
        if (idx === -1) return { title: line, note: "" };
        return { title: line.slice(0, idx).trim(), note: line.slice(idx + 1).trim() };
      });
  return text;
}
